import {faCopy, faEdit, faSave, faTrashCan} from '@fortawesome/pro-regular-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Button, Input, Popover, Tag} from 'antd';
import {useCallback, useMemo, useState} from 'react';

import {useDomainConfig} from '../../../../provider/domain-config/domain-config-hook';
import {DomainNameEnum} from '../../../../provider/domain-config/domain-config-type';
import {Locale} from '../../../../provider/locale/locale';
import {useLocale} from '../../../../provider/locale/locale-hook';
import {useMessage} from '../../../../provider/message/message-hook';
import {
    useRemoveWebhookMutation,
    useUpdateWebhookMutation,
} from '../../../../service/webhook-integration/use-webhook-integrations';
import {WebhookIntegrationItemType} from '../../../../service/webhook-integration/webhook-integration-type';

import * as styles from './webhook-integration.scss';

type PropsType = {
    webhookItem: WebhookIntegrationItemType;
    onWebhookDeleted: (id: number) => void;
};

export function WebhookIntegrationItem(props: PropsType): JSX.Element {
    const {webhookItem, onWebhookDeleted} = props;

    const [savedName, setSavedName] = useState<string>(webhookItem.name);
    const {domainName} = useDomainConfig();

    const {getLocalizedString} = useLocale();

    const {message} = useMessage();
    const [isEditMode, setIsEditMode] = useState<boolean>(false);
    const [currentName, setCurrentName] = useState<string>(savedName);

    const {mutate: updateWebhook, isLoading: isUpdatingWebhook} = useUpdateWebhookMutation();
    const {mutate: removeWebhook, isLoading: isRemovingWebhook} = useRemoveWebhookMutation();

    const webhookUrl = useMemo(() => {
        const serviceName = domainName === DomainNameEnum.brandWizard ? 'brandwizard' : 'rocketdata';

        return `https://webhook.${serviceName}.io/v1/reviews_generator/${webhookItem.webhookId}/new/`;
    }, [domainName, webhookItem.webhookId]);

    const handleUpdate = useCallback(() => {
        if (currentName === savedName || !currentName) {
            setIsEditMode(false);
            setCurrentName(savedName);
            return;
        }

        updateWebhook(
            {
                id: webhookItem.id,
                name: currentName,
            },
            {
                onSuccess: () => {
                    setSavedName(currentName);
                    message.info(getLocalizedString('TEXT__SAVED'));
                },
                onError: () => {
                    setCurrentName(savedName);
                    message.info(getLocalizedString('ERROR__SOMETHING_WENT_WRONG'));
                },
            }
        );
        setIsEditMode(false);
    }, [currentName, getLocalizedString, message, savedName, updateWebhook, webhookItem.id]);

    const handleRemove = useCallback(() => {
        removeWebhook(webhookItem.id, {
            onSuccess: () => {
                onWebhookDeleted(webhookItem.id);
                message.info(getLocalizedString('INTEGRATIONS__WEBHOOK__REMOVED'));
            },
            onError: () => {
                message.error(getLocalizedString('ERROR__SOMETHING_WENT_WRONG'));
            },
        });
    }, [getLocalizedString, message, onWebhookDeleted, removeWebhook, webhookItem.id]);

    const handleCopyUrl = useCallback(async () => {
        await navigator.clipboard.writeText(webhookUrl);
        message.info(getLocalizedString('TEXT__COPIED'));
    }, [message, webhookUrl, getLocalizedString]);

    return (
        <div className={styles.WebhookIntegrationContent_webhookItemContainer} key={webhookItem.id}>
            {isEditMode ? (
                <Input onChange={(event) => setCurrentName(event.target.value)} value={currentName} />
            ) : (
                <div className={styles.WebhookIntegrationContent_webhookItemText}>
                    <Popover arrow={false} content={savedName}>
                        {savedName}
                    </Popover>
                </div>
            )}

            <div className={styles.WebhookIntegrationContent_webhookItemText}>
                <Popover arrow={false} content={webhookUrl}>
                    {webhookUrl}
                </Popover>
            </div>

            <Tag
                className={styles.WebhookIntegrationContent_webhookItemStatus}
                color={webhookItem.canBeDeleted ? 'default' : 'success'}
            >
                <Locale
                    stringKey={
                        webhookItem.canBeDeleted ? 'INTEGRATIONS__WEBHOOK__NOT_USED' : 'INTEGRATIONS__WEBHOOK__USED'
                    }
                />
            </Tag>

            <div className={styles.WebhookIntegrationContent_webhookItemActions_wrapper}>
                <Button
                    className={styles.WebhookIntegrationContent_webhookItemActions_actionButton}
                    icon={<FontAwesomeIcon icon={faCopy} />}
                    onClick={handleCopyUrl}
                    type="text"
                />

                {isEditMode ? (
                    <Button
                        className={styles.WebhookIntegrationContent_webhookItemActions_actionButton}
                        icon={<FontAwesomeIcon icon={faSave} />}
                        loading={isUpdatingWebhook}
                        onClick={handleUpdate}
                        type="text"
                    />
                ) : (
                    <Button
                        className={styles.WebhookIntegrationContent_webhookItemActions_actionButton}
                        icon={<FontAwesomeIcon icon={faEdit} />}
                        loading={isUpdatingWebhook}
                        onClick={() => setIsEditMode(true)}
                        type="text"
                    />
                )}

                <Button
                    className={styles.WebhookIntegrationContent_webhookItemActions_actionButton}
                    disabled={!webhookItem.canBeDeleted}
                    icon={<FontAwesomeIcon icon={faTrashCan} />}
                    loading={isRemovingWebhook}
                    onClick={handleRemove}
                    type="text"
                />
            </div>
        </div>
    );
}
